import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { QrDoProduto } from '../../api/produtos.js';
import { DesenhoDaEtiqueta } from './DesenhoDaEtiqueta.js';
import type { EtiquetaParaImprimir, TamanhoDeEtiqueta } from './etiqueta.js';
import { regraDaPagina } from './etiqueta.js';
import './FolhaDeImpressao.css';

type Props = {
  readonly tamanho: TamanhoDeEtiqueta;
  readonly etiquetas: readonly EtiquetaParaImprimir[];
  readonly qrs: ReadonlyMap<string, QrDoProduto>;
};

/**
 * O que vai para a impressora: uma etiqueta por página, página do tamanho da
 * etiqueta (`@page` em milímetros), sem margem.
 *
 * Mora fora do `#root`, num portal no `<body>`: na impressão o painel inteiro
 * sai de cena (`display: none`) e só esta folha fica, sem página em branco
 * herdada da altura da tela. Na tela ela não aparece — a pré-visualização é a
 * grade de cartões —, e por isso sai da árvore de acessibilidade.
 *
 * Imprimir pelo navegador, e não gerar PDF aqui: a janela de impressão já
 * oferece "Salvar como PDF" e a impressora de etiquetas, e o tamanho em
 * milímetros chega ao driver sem nenhuma biblioteca a mais.
 */
export function FolhaDeImpressao({ tamanho, etiquetas, qrs }: Props) {
  return (
    <FolhaDeEtiquetas tamanho={tamanho}>
      {etiquetas.map((etiqueta) => {
        const qr = qrs.get(etiqueta.codPro);
        return qr === undefined ? null : (
          <div key={etiqueta.chave} className="folha-etiquetas__pagina">
            <DesenhoDaEtiqueta tamanho={tamanho} qr={qr} emMilimetros />
          </div>
        );
      })}
    </FolhaDeEtiquetas>
  );
}

/**
 * A folha em si — portal no `<body>` com a regra `@page` do tamanho. Serve à
 * etiqueta de produto e à de lote: cada página é um
 * `.folha-etiquetas__pagina` com o desenho em milímetros dentro.
 */
export function FolhaDeEtiquetas({
  tamanho,
  children,
}: {
  readonly tamanho: TamanhoDeEtiqueta;
  readonly children: ReactNode;
}) {
  return createPortal(
    <div className="folha-etiquetas" data-folha-de-impressao aria-hidden="true">
      <style>{regraDaPagina(tamanho)}</style>
      {children}
    </div>,
    document.body,
  );
}
